import {useEffect} from 'react'
import {createUseStyles} from 'react-jss'
import {useNavigate, useParams} from "react-router-dom";
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import {formatDateString, formatDateTimeString} from '../utils/dateHelper'
import useEmployeeActions from '../hooks/useEmployeeActions';
import {useAppSelector} from '../redux/hooks';
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator';
import ShiftListComponent from '../components/ShiftList';
import PayDeviationListComponent from '../components/PayDeviationList';
import NavLink from '../components/NavLink';
import {faArrowLeft} from '@fortawesome/free-solid-svg-icons';
import Link from '../components/Link';
import {titleMarginBottom} from '../constants/layout';
import Divider from "../components/Divider";
import Switch from '../components/Switch';
import useCommunicationOptOutActions from '../hooks/useCommunicationOptOutActions';
import EmploymentContractList from "../components/EmploymentContractList";
import OpenShiftList from "../components/OpenShiftList";
import DetailValueRow from '../components/DetailValueRow';


const EmployeeDetails = () => {
  const styles = useStyles()
  const params = useParams<{ id: string }>()
  const navigate = useNavigate();

  const {getEmployeeDetails} = useEmployeeActions()
  const {setCommunicationOptOutStateForEmployee} = useCommunicationOptOutActions()

  const loading = useAppSelector(state => state.employee.loading)
  const employeeDetails = useAppSelector(state => state.employee.employeeDetails)
  const optOuts = useAppSelector(state => state.optOut.optOuts)

  useEffect(() => {
    if ((employeeDetails === null || employeeDetails.id !== params.id) && params.id) {
      getEmployeeDetails(params.id)
    }
  }, [])

  const notSpecified = 'Not specified'
  const inherited = '(inherited)'
  const employeePayoutSettingTooltip = 'Employee-specific payout setting overrides inherited value'

  const getEmployeePayoutSettingSeverity = (value: unknown) => {
    return value != null ? 'warning' : undefined
  }

  const renderCommunicationOptOutSwitch = () => {
    return (
      <Switch
        id='communicationOptOutSwitch'
        key='communicationOptOutSwitch'
        checked={(optOuts?.employeeIds?.find(x => x === employeeDetails?.id) !== undefined)}
        label='Communication opt-out'
        onChange={() => handleCommunicationOptOutStateChange()}/>
    )
  }

  const handleCommunicationOptOutStateChange = () => {
    if (employeeDetails) {
      var currentState = (optOuts?.employeeIds?.find(x => x === employeeDetails?.id) !== undefined)
      setCommunicationOptOutStateForEmployee(employeeDetails.id as string, !currentState)
    }
  }

  const renderUserLink = () => {
    if (employeeDetails?.connectedUser)
      return (
        <Link
          to={`/users/${employeeDetails.connectedUser.id}`}>{employeeDetails.connectedUser.givenName} {employeeDetails.connectedUser.familyName}</Link>
      )
    else
      return (<span>Not connected</span>)
  }

  const renderPayoutSettingsCardBody = () => {
    return (
      <div>
        <DetailValueRow
          label='Min amount per payout'
          value={employeeDetails?.payoutSettings?.minAmountPerPayout != null ? employeeDetails?.payoutSettings?.minAmountPerPayout : `${employeeDetails?.effectivePayoutSettings?.minAmountPerPayout} ${inherited}`}
          severity={getEmployeePayoutSettingSeverity(employeeDetails?.payoutSettings?.minAmountPerPayout)}
          tooltip={employeePayoutSettingTooltip}
        />
        <DetailValueRow
          label='Max amount per period'
          value={employeeDetails?.payoutSettings?.maxAmountPerPayday != null ? employeeDetails?.payoutSettings?.maxAmountPerPayday : `${employeeDetails?.effectivePayoutSettings?.maxAmountPerPayday} ${inherited}`}
          severity={getEmployeePayoutSettingSeverity(employeeDetails?.payoutSettings?.maxAmountPerPayday)}
          tooltip={employeePayoutSettingTooltip}
        />
        <DetailValueRow
          label='Max percent of earnings'
          value={employeeDetails?.payoutSettings?.maxPercentPerPayday != null ? employeeDetails?.payoutSettings?.maxPercentPerPayday : `${employeeDetails?.effectivePayoutSettings?.maxPercentPerPayday} ${inherited}`}
          severity={getEmployeePayoutSettingSeverity(employeeDetails?.payoutSettings?.maxPercentPerPayday)}
          tooltip={employeePayoutSettingTooltip}
        />
        <DetailValueRow
          label='Max number of payouts per period'
          value={employeeDetails?.payoutSettings?.maxNumberOfPayoutsPerPayday != null ? employeeDetails?.payoutSettings?.maxNumberOfPayoutsPerPayday : `${employeeDetails?.effectivePayoutSettings?.maxNumberOfPayoutsPerPayday} ${inherited}`}
          severity={getEmployeePayoutSettingSeverity(employeeDetails?.payoutSettings?.maxNumberOfPayoutsPerPayday)}
          tooltip={employeePayoutSettingTooltip}
        />
        <DetailValueRow
          label='Payout fee'
          value={employeeDetails?.payoutSettings?.fee != null ? employeeDetails?.payoutSettings?.fee : `${employeeDetails?.effectivePayoutSettings?.fee} ${inherited}`}
          severity={getEmployeePayoutSettingSeverity(employeeDetails?.payoutSettings?.fee)}
          tooltip={employeePayoutSettingTooltip}
        />
        <DetailValueRow
          label='Payout currency'
          value={employeeDetails?.payoutSettings?.currency != null ? employeeDetails?.payoutSettings?.currency?.toUpperCase() : `${employeeDetails?.effectivePayoutSettings?.currency?.toUpperCase()} ${inherited}`}
          severity={getEmployeePayoutSettingSeverity(employeeDetails?.payoutSettings?.currency)}
          tooltip={employeePayoutSettingTooltip}
        />
        <DetailValueRow
          label='Payout limitation reason'
          value={!!employeeDetails?.payoutLimitationReason ? employeeDetails.payoutLimitationReason : notSpecified}
          severity={!!employeeDetails?.payoutLimitationReason ? 'warning' : undefined}
          tooltip='Employee has a payout limitation reason'
        />
      </div>
    )
  }

  if (!employeeDetails) {
    return (
      <div className={styles.container}>
        <FullScreenLoadingIndicator visible={loading}/>
        <h1>Employee</h1>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <h1>Employee: {employeeDetails.givenName} {employeeDetails.familyName}</h1>
      <div className={styles.actionBar}>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-start'}}>
          <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)}/>
        </div>
        <div className={styles.actionBarCell} style={{justifyContent: 'flex-end'}}>
          <div style={{paddingBottom: 8}}>
            {renderCommunicationOptOutSwitch()}
          </div>
        </div>
      </div>
      <CardSection>
        <DetailsCard>
          <h6>General information</h6>
          <p>Employer: <Link to={`/employers/${employeeDetails.employerId}`}>{employeeDetails.employerName}</Link></p>
          <p>External id: <span>{employeeDetails.externalId || notSpecified}</span></p>
          <p>Employment number: <span>{employeeDetails.employmentNumber || notSpecified}</span></p>
          <p>Name: <span>{employeeDetails.givenName || notSpecified} {employeeDetails.familyName}</span></p>
          <p>Identification number: <span>{employeeDetails.identificationNumber || notSpecified}</span></p>
          <p>Email: <span>{employeeDetails.email || notSpecified}</span></p>
          <p>Phone number: <span>{employeeDetails.phoneNumber || notSpecified}</span></p>
          <p>Type: <span>{employeeDetails.type || notSpecified}</span></p>
          <p>Payroll report identifier: <span>{employeeDetails.payrollFileIdentifier || notSpecified}</span></p>
          <p>Bank account number: <span>{employeeDetails.bankAccountNumber || notSpecified}</span></p>
          <p>Department: <span>{employeeDetails.department || notSpecified}</span></p>
          <p>Category: <span>{employeeDetails.category || notSpecified}</span></p>
          <p>Location: <span>{employeeDetails.location || notSpecified}</span></p>
          <p>Cost center: <span>{employeeDetails.costCenter || notSpecified}</span></p>
          <p>Tax table identifier: <span>{employeeDetails.taxSettings?.taxTableIdentifier || notSpecified}</span></p>
          <p>Fixed tax rate percent: <span>{employeeDetails.taxSettings?.fixedTaxRatePercent != null ? employeeDetails.taxSettings?.fixedTaxRatePercent : notSpecified}</span></p>
          <p>Payout tax in percent: <span>{employeeDetails.taxSettings?.payoutTaxInPercent != null ? employeeDetails.taxSettings?.payoutTaxInPercent : notSpecified}</span></p>
        </DetailsCard>
        <DetailsCard>
          <h6>State and user information</h6>
          <DetailValueRow
            label='Non-eligible'
            value={employeeDetails.nonEligible ? 'yes' : 'no'}
            severity={employeeDetails.nonEligible ? 'danger' : undefined}
            tooltip='Employee is marked as non-eligible'
          />
          <DetailValueRow
            label='State'
            value={employeeDetails.status || notSpecified}
            severity={employeeDetails.status !== 'active' ? 'warning' : undefined}
            tooltip='Employee state is not active'
          />
          <p>Activation date: <span>{employeeDetails.activated ? formatDateTimeString(employeeDetails.activationDate) : notSpecified}</span></p>
          <p>Delay termination until payday: <span>{employeeDetails.extendTerminationToUpcomingPayday ? 'yes' : 'no'}</span></p>
          <DetailValueRow
            label='Paused'
            value={employeeDetails.paused ? 'yes' : 'no'}
            severity={employeeDetails.paused ? 'danger' : undefined}
            tooltip='Employee is paused'
          />
          {employeeDetails.paused &&
              <p>Pause date: <span>{formatDateTimeString(employeeDetails.pauseDate)}</span></p>}
          {employeeDetails.paused &&
              <p>Pause reason: <span>{employeeDetails.pauseReason || notSpecified}</span></p>}
          <p>Connected to user: {renderUserLink()}</p>
          {employeeDetails.connectedUser &&
              <p>Connected date: {formatDateTimeString(employeeDetails.connectedUser.connectedDate)}</p>
          }
          {employeeDetails.pendingBankAccountActivation &&
              <DetailValueRow
                label='Bank account activation pending'
                value='yes'
                severity='warning'
                tooltip='Bank account activation is pending'
              />
          }
          {employeeDetails.bankAccountActivationFailed &&
              <DetailValueRow
                label='Bank account activation failed'
                value='yes'
                severity='danger'
                tooltip='Bank account activation failed'
              />
          }
          <DetailValueRow
            label='Under foreclosure'
            value={employeeDetails.underForeclosure ? 'yes' : 'no'}
            severity={employeeDetails.underForeclosure ? 'danger' : undefined}
            tooltip='Employee is under foreclosure'
          />
          <DetailValueRow
            label='Display payout limitation info'
            value={employeeDetails.displayPayoutLimitationInfo ? 'yes' : 'no'}
            severity={employeeDetails.displayPayoutLimitationInfo ? 'warning' : undefined}
            tooltip='Payout limitation info is displayed to the employee'
          />
          <Divider height={20}/>
          <p>Welcome message sent: <span>{employeeDetails.welcomeMessageSent ? "yes" : "no"}</span></p>
          <p>Eligible for activation
            reminder: <span>{employeeDetails.isEligibleForActivationReminder ? "yes" : "no"}</span></p>
          <p>Last activation
            reminder: <span>{employeeDetails.lastActivationReminderDate ? formatDateString(employeeDetails.lastActivationReminderDate) : notSpecified}</span>
          </p>
          <p>Next activation
            reminder: <span>{employeeDetails.nextActivationReminderDate ? formatDateString(employeeDetails.nextActivationReminderDate) : notSpecified}</span>
          </p>
        </DetailsCard>
        <DetailsCard>
          <h6>Payout settings</h6>
          {renderPayoutSettingsCardBody()}
        </DetailsCard>
      </CardSection>

      {!!employeeDetails.internalComment &&
          <>
              <h4>Internal comment</h4>
              <DetailsCard>
                  <p style={{whiteSpace: 'pre-wrap'}}>
                    {employeeDetails.internalComment}
                  </p>
              </DetailsCard>
          </>
      }

      <h4>Contracts</h4>
      <DetailsCard>
        <EmploymentContractList employmentContracts={employeeDetails.employmentContracts}/>
      </DetailsCard>

      <h4>Shifts</h4>
      <DetailsCard>
        <ShiftListComponent employeeId={employeeDetails.id}/>
      </DetailsCard>

      <h4>Deviations</h4>
      <DetailsCard>
        <PayDeviationListComponent employeeId={employeeDetails.id}/>
      </DetailsCard>

      {employeeDetails.openShifts && employeeDetails.openShifts.length > 0 && (
        <>
          <h4>Open Shifts</h4>
          <DetailsCard>
            <OpenShiftList openShifts={employeeDetails.openShifts}/>
          </DetailsCard>
        </>
      )}

      <FullScreenLoadingIndicator visible={loading}/>
    </div>
  )
}

export default EmployeeDetails

const useStyles = createUseStyles({
  container: {
    marginBottom: 20,
    '& h1': {
      marginBottom: titleMarginBottom
    },
    '& h4': {
      marginTop: '15px'
    }
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: -8
  },
  actionBarCell: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: 15,
    alignItems: 'center',
    width: '33%'
  },
})
